import { useEffect, useState, useCallback } from "react";
import { X, Share, PlusSquare, Download } from "lucide-react";

/* AddToHomeScreenPrompt
 * ----------------------
 * Cross-platform "Install Rivones" PWA prompt.
 *
 * Android / Chromium / Edge: listens for the native `beforeinstallprompt`
 * event and shows a CTA that triggers the OS install dialog.
 *
 * iOS Safari (iPhone/iPad): iOS never fires `beforeinstallprompt`, so we
 * detect Safari + iOS and show a manual instruction card pointing at the
 * Share icon → "Add to Home Screen".
 *
 * The prompt:
 *  - never shows when the app is already running in standalone mode
 *  - never shows on the /login / OAuth callback screens (would block auth)
 *  - is dismissible; we remember the dismissal in localStorage and stay quiet
 *    for 14 days
 *  - is gently delayed (8 s after mount) to avoid hijacking the first visit
 */

const DISMISS_KEY = "rivones_a2hs_dismissed_at";
const DISMISS_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;
const DELAY_MS = 8000;

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  if (window.navigator?.standalone === true) return true; // iOS Safari
  return false;
};

const isIosSafari = () => {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  const isIos = /iPad|iPhone|iPod/.test(ua) ||
    (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return isIos && isSafari;
};

const wasRecentlyDismissed = () => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < DISMISS_WINDOW_MS;
  } catch (_e) {
    return false;
  }
};

const markDismissed = () => {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch (_e) {
    /* private mode — ignore */
  }
};

export default function AddToHomeScreenPrompt() {
  const [deferredEvent, setDeferredEvent] = useState(null);
  const [variant, setVariant] = useState(null); // 'android' | 'ios' | null
  const [visible, setVisible] = useState(false);

  // Hide on auth-critical routes to avoid blocking login UX.
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  const onAuthScreen = pathname === "/login" || pathname.startsWith("/reset-password");

  // 1) Listen for Chromium's beforeinstallprompt (Android + desktop Chrome/Edge)
  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed() || onAuthScreen) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredEvent(e);
      setVariant("android");
      // Surface the prompt after a small delay so it doesn't compete with
      // the first paint.
      setTimeout(() => setVisible(true), DELAY_MS);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [onAuthScreen]);

  // 2) iOS Safari fallback — no event ever fires, so we proactively schedule it.
  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed() || onAuthScreen) return;
    if (deferredEvent) return; // android path already handling it
    if (!isIosSafari()) return;
    const t = setTimeout(() => {
      setVariant("ios");
      setVisible(true);
    }, DELAY_MS);
    return () => clearTimeout(t);
  }, [deferredEvent, onAuthScreen]);

  // 3) Hide on successful install
  useEffect(() => {
    const onInstalled = () => {
      setVisible(false);
      setDeferredEvent(null);
      markDismissed();
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredEvent) return;
    deferredEvent.prompt();
    try {
      await deferredEvent.userChoice;
    } catch (_e) {
      /* user cancelled */
    }
    setDeferredEvent(null);
    setVisible(false);
    markDismissed();
  }, [deferredEvent]);

  const handleDismiss = useCallback(() => {
    setVisible(false);
    markDismissed();
  }, []);

  if (!visible || !variant) return null;

  return (
    <div
      data-testid="a2hs-prompt"
      className="fixed left-3 right-3 bottom-[88px] sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:bottom-6 sm:w-[420px] z-[60] animate-in fade-in slide-in-from-bottom-4 duration-500"
      role="dialog"
      aria-labelledby="a2hs-title"
    >
      <div className="relative rounded-2xl bg-charcoal text-sand shadow-2xl ring-1 ring-white/10 overflow-hidden">
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Cerrar"
          data-testid="a2hs-dismiss-btn"
          className="absolute top-2.5 right-2.5 p-1.5 rounded-full text-sand/60 hover:text-sand hover:bg-white/10 transition"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3 p-4 pr-10">
          <div className="shrink-0 w-12 h-12 rounded-xl bg-terracotta/90 flex items-center justify-center shadow-inner shadow-black/30">
            <img
              src="/assets/icon-192.png"
              alt="Rivones"
              className="w-9 h-9 rounded-md"
            />
          </div>
          <div className="min-w-0">
            <h3
              id="a2hs-title"
              className="font-semibold text-sand text-[15px] leading-snug"
            >
              Instala Rivones en tu pantalla de inicio
            </h3>
            <p className="text-sand/70 text-[13px] mt-1 leading-relaxed">
              {variant === "android"
                ? "Acceso de un toque, reservas más rápidas y notificaciones de tus viajes."
                : "Para una experiencia tipo app, agrega Rivones a tu pantalla de inicio."}
            </p>
          </div>
        </div>

        {variant === "android" ? (
          <div className="px-4 pb-4 flex gap-2">
            <button
              type="button"
              onClick={handleInstall}
              data-testid="a2hs-install-btn"
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-terracotta hover:bg-terracotta/90 active:bg-terracotta/80 text-white font-medium text-sm py-2.5 transition"
            >
              <Download className="w-4 h-4" />
              Instalar app
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              data-testid="a2hs-later-btn"
              className="px-3 rounded-xl text-sand/70 hover:text-sand hover:bg-white/5 text-sm transition"
            >
              Después
            </button>
          </div>
        ) : (
          <div className="px-4 pb-4">
            <ol className="space-y-2 text-[13px] text-sand/85">
              <li className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/10 text-[11px] font-semibold">
                  1
                </span>
                Toca el botón
                <Share
                  className="w-4 h-4 text-sky-300 inline -mt-px"
                  aria-hidden="true"
                />
                <span className="text-sand/70">Compartir</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/10 text-[11px] font-semibold">
                  2
                </span>
                Elige
                <PlusSquare
                  className="w-4 h-4 text-sand/90 inline -mt-px"
                  aria-hidden="true"
                />
                <span className="text-sand/90 font-medium">
                  Agregar a inicio
                </span>
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/10 text-[11px] font-semibold">
                  3
                </span>
                <span>
                  Confirma con <span className="font-medium">Agregar</span>
                </span>
              </li>
            </ol>
            <button
              type="button"
              onClick={handleDismiss}
              data-testid="a2hs-got-it-btn"
              className="mt-3 w-full rounded-xl bg-white/10 hover:bg-white/15 text-sand text-sm py-2 transition"
            >
              Entendido
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
